import { getCookie, PendoXSRFToken, PendoXSRFTokenC80 } from '@/utils/cookies.utils';
import * as WorkerUtils from '@/session-replay/replay-worker.util';
import { storage } from '@/session-replay/replay-worker.storage';

export const UNRENDERABLE_CHUNK_ERROR = 'Replay chunk is missing required Meta or FullSnapshot events';

// rrweb EventType values — inlined so the worker bundle does not import @pendo/rrweb (requires window)
const RRWEB_META_EVENT_TYPE = 4;
const RRWEB_FULL_SNAPSHOT_EVENT_TYPE = 2;

export const DEFAULT_EXPIRE_TIME = 60 * 30 * 1000; // 30 minutes in ms

export const LIVE_EXPIRE_TIME = 3 * 60 * 60 * 1000; // 3 hours in ms — live event feed TTL

// Gap in stored chunks taken to separate one of a visitor's live sessions from the next. Matches
// the backend's LiveReplayVisitorTTL, past which a silent visitor is no longer live.
export const LIVE_SESSION_GAP_MS = 60 * 60 * 1000;

export const CONCURRENCY_LIMIT = 6; // number of concurrent fetch requests we'll make to avoid overloading the BE

export const MAX_BATCH_SIZE = 100 * 1024 * 1024; // 100mb in bytes

export const MIN_BATCH_SIZE = 20 * 1024 * 1024; // 20mb in bytes

export const WORKER_TO_MAIN_THREAD_MESSAGE = {
    BATCH_TIME_RANGE: 'batchTimeRange',
    CHUNK_SIZE: 'chunkSize',
    PARSED_EVENT: 'parsedEvent',
    HOSTED_RESOURCE_MAP: 'hostedResourceMap',
    REPLAY_LOAD_ERROR: 'replayLoadError',
    SAVED_TO_IDB: 'savedToIdb',
    DONE_LOADING_CHUNKS: 'doneLoadingChunks',
    IDB_OPEN_FAILED: 'idbOpenFailed',
    LIVE_SAVED_TO_IDB: 'liveSavedToIdb',
    LIVE_MEMORY_FALLBACK: 'liveMemoryFallback',
    LIVE_CURSORS: 'liveCursors',
    LIVE_SESSION_EVENTS: 'liveSessionEvents',
};

// Main-thread → worker message types for the live event feed persistence path.
export const LIVE_WORKER_MESSAGE = {
    SAVE_LIVE_BATCH: 'saveLiveBatch',
    READ_LIVE_CURSORS: 'readLiveCursors',
    READ_LIVE_SESSION_EVENTS: 'readLiveSessionEvents',
};

// Redis stream entry ids are `${ms}-${seq}`; both parts are numeric and the seq is not zero-padded,
// so they must be compared numerically, not lexicographically. Null for anything unparseable, so
// callers decide rather than inheriting 0/0 — the earliest id there is.
const parseEntryId = (id) => {
    const [ms, seq] = String(id).split('-');
    const milliseconds = Number(ms);

    if (!ms || !Number.isFinite(milliseconds)) return null;

    return [milliseconds, Number(seq) || 0];
};

/**
 * Whether `candidate` is a later Redis stream entry id than `current`. A missing `current` means
 * nothing seen yet. Used to drop already-consumed events and to fold per-chunk ids into a max.
 * @param {string} candidate
 * @param {string|undefined} current
 * @returns {boolean}
 */
export const isNewerLiveEntryId = (candidate, current) => {
    if (!current) return true;

    const candidateId = parseEntryId(candidate);
    const currentId = parseEntryId(current);

    if (!candidateId) return false;
    if (!currentId) return true;

    return candidateId[0] > currentId[0] || (candidateId[0] === currentId[0] && candidateId[1] > currentId[1]);
};

// When the backend appended a chunk, from the ms half of its entry id. Null if unparseable.
export const liveEntryTimestamp = (id) => parseEntryId(id)?.[0] ?? null;

// Sorts a live session's persisted batches back into arrival order. An unparseable id sorts last:
// treated as the earliest it would replay a batch's events ahead of real history.
export const compareLiveEntryIds = (first, second) => {
    const firstId = parseEntryId(first);
    const secondId = parseEntryId(second);

    if (!firstId && !secondId) return 0;
    if (!firstId) return 1;
    if (!secondId) return -1;

    return firstId[0] - secondId[0] || firstId[1] - secondId[1];
};

/**
 * Returns the index of the chunk that a given timestamp belongs to
 * @param {Number} timestamp - recording event timestamp
 * @param {import('./helpers/common.util').chunk[]} chunks - full list of chunks
 * @returns {Number} index of the chunk that a given timestamp belongs to
 */
export const findChunkIndex = ({ timestamp, chunks }) => {
    let left = 0;
    let right = chunks.length - 1;
    while (left <= right) {
        const mid = Math.floor((left + right) / 2);
        if (timestamp < chunks[mid].chunkStartTime) {
            right = mid - 1;
        } else if (timestamp > chunks[mid].chunkEndTime) {
            left = mid + 1;
        } else {
            return mid;
        }
    }

    return -1;
};

export const calculateRemainingStorageInBytes = async () => {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
        try {
            const { usage, quota } = await navigator.storage.estimate();
            const remainingSpace = quota - usage;
            storage.set(0.75 * remainingSpace);
        } catch (error) {
            console.error(`Error calculating remaining space in storage: ${error}`);
        }
    } else {
        console.error('StorageManager API is not supported in this browser.');
    }
};

export const createBatchesByNumber = ({ missingChunks, numOfRecordingIdsPerBatch = 6 }) => {
    const batchesByNumber = [];
    for (let i = 0; i < missingChunks.length; i += numOfRecordingIdsPerBatch) {
        const batch = missingChunks.slice(i, i + numOfRecordingIdsPerBatch).map((chunk) => chunk.recordingId);
        batchesByNumber.push(batch);
    }

    return batchesByNumber;
};

export const createBatchesBySize = ({ missingChunks }) => {
    const batchesBySize = [];
    let currentBatchRecordingIds = [];
    let currentBatchSize = 0;

    for (const { recordingId, chunkSize } of missingChunks) {
        // start a new batch if adding the current chunk will go over the max batch size
        const willExceedBatchLimit = currentBatchSize + chunkSize > MAX_BATCH_SIZE;
        if (willExceedBatchLimit && currentBatchRecordingIds.length > 0) {
            batchesBySize.push(currentBatchRecordingIds);
            currentBatchRecordingIds = [];
            currentBatchSize = 0;
        }

        currentBatchRecordingIds.push(recordingId);
        currentBatchSize += chunkSize;
    }

    // push any remaining items
    if (currentBatchRecordingIds.length > 0) {
        batchesBySize.push(currentBatchRecordingIds);
    }

    return batchesBySize;
};

/**
 * @typedef {String[]} batch
 * @property {String} recordingId
 */

/**
 * Iterates through missingChunks to create batches
 * @param {import('./helpers/common.util').chunk[]} missingChunks - list of chunks that are missing in IndexedDB
 * @returns {batch[]} array of batches
 */
export const createBatchesToDownload = ({ missingChunks }) => {
    const isMissingChunkSize = missingChunks.some((chunk) => chunk.chunkSize === undefined);
    const batches = isMissingChunkSize
        ? WorkerUtils.createBatchesByNumber({ missingChunks })
        : WorkerUtils.createBatchesBySize({ missingChunks });

    return batches;
};

/**
 * Builds the IndexedDB key for a chunk. Regular replays key by recordingSessionId + recordingId;
 * saved clips key by clipId + recordingId (the only IDB-key difference between the two flows).
 * @param {{ recordingSessionId?: string, recordingId: string, clipId?: string }} params
 * @returns {string}
 */
export function getChunkKey ({ recordingSessionId, recordingId, clipId }) {
    return clipId ? `savedclip:${clipId}:${recordingId}` : `${recordingSessionId}${recordingId}`;
}

/**
 * Builds the IndexedDB key for a live event-feed chunk (in the dedicated live DB). Unlike recorded
 * replays (one recordingId = one chunk), the live feed streams a recording's events incrementally, so
 * lastEntryId (the batch's last Redis stream entry id — unique and monotonic per recording) uniquely
 * identifies each batch. batchStartTime is kept in the chunk metadata for the time index, not the key.
 * @param {{ liveSessionId: string, recordingId: string, lastEntryId?: string }} params
 * @returns {string}
 */
export function getLiveChunkKey ({ liveSessionId, recordingId, lastEntryId }) {
    return `${liveSessionId}:${recordingId}:${lastEntryId}`;
}

/**
 * Fetches events for the given recordingIds and returns a Response-like object whose json() resolves
 * to a { [recordingId]: payload } map — matching fetchRecordingEvents' shape so the worker pipeline is
 * identical for both flows. Saved clips differ only here: the clip endpoint returns a single merged
 * events array per request, so we fetch each recordingId in its OWN request and key the results. Fetching
 * per-recordingId (rather than merging a batch and splitting it) guarantees each stored chunk keeps its
 * own Meta + FullSnapshot — splitting a merged response by time could misassign a recording's snapshot to
 * the previous chunk, leaving a chunk unrenderable. We fetch sequentially so a batch with several
 * recordingIds doesn't burst (fetchMissingChunks already runs CONCURRENCY_LIMIT batches in parallel).
 */
export async function fetchEventsForRecordingIds ({
    replayContext,
    clipId,
    recordingIds,
    cookie,
    subId,
    baseUrl,
    useRecordingMapId = true,
    signal,
}) {
    if (clipId) {
        const eventsByRecordingId = {};
        for (const recordingId of recordingIds) {
            // eslint-disable-next-line no-await-in-loop
            eventsByRecordingId[recordingId] = await WorkerUtils.fetchSavedClipEvents({
                clipId, recordingIds: [recordingId], subId, baseUrl, cookie, signal,
            });
        }

        return { json: async () => eventsByRecordingId };
    }

    return WorkerUtils.fetchRecordingEvents({ replayContext, cookie, subId, baseUrl, recordingIds, useRecordingMapId, signal });
}

/*
 * A Lot of functionality copied from pendo-replayer.utils.js and common.util.js with modifications for it to work with
 * fetch api instead of Pendo http wrapper
 */
export async function fetchRecordingEvents ({
    replayContext,
    cookie,
    subId,
    baseUrl,
    recordingIds,
    useRecordingMapId = true,
    signal,
}) {
    // extract the required params for fetching recording events
    const { maxBrowserTime, minBrowserTime, visitorId, appId } = replayContext;

    // required cookies
    const X_PENDO_XSRF_TOKEN = 'X-Pendo-Xsrf-Token';
    const b64VisitorId = `b64_${b64EncodeUnicode(visitorId)}`;
    const url = `/${getReplayBaseUrl(appId, subId)}/${b64VisitorId}`;
    const pendoXsrfCookieName = getCookie(cookie, PendoXSRFToken) ? PendoXSRFToken : PendoXSRFTokenC80;

    const params = `?recordingIdMap=${useRecordingMapId}`;
    const response = await fetch(`${baseUrl}${url}${params}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            [X_PENDO_XSRF_TOKEN]: getCookie(cookie, pendoXsrfCookieName),
            'X-Pendo-Base64-Encoded-Params': true,
        },
        cookie,
        body: JSON.stringify({
            recordingIds,
            maxBrowserTime,
            minBrowserTime,
        }),
        signal,
    });

    if (!response.ok) {
        throw new Error(`Request failed with status: ${response.status}`);
    }

    return response;
}

export function getReplayBaseUrl (appId, subId) {
    return `api/session-replay/s/${subId}/application/${appId}`;
}

export async function fetchSavedClipEvents ({ clipId, recordingIds, subId, baseUrl, cookie, signal }) {
    const X_PENDO_XSRF_TOKEN = 'X-Pendo-Xsrf-Token';
    const pendoXsrfCookieName = getCookie(cookie, PendoXSRFToken) ? PendoXSRFToken : PendoXSRFTokenC80;
    const response = await fetch(`${baseUrl}/api/session-replay/s/${subId}/clip/${clipId}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            [X_PENDO_XSRF_TOKEN]: getCookie(cookie, pendoXsrfCookieName),
        },
        body: JSON.stringify({ recordingIds }),
        signal,
    });

    if (!response.ok) {
        throw new Error(`Request failed with status: ${response.status}`);
    }

    return response.json();
}

export function b64EncodeUnicode (str) {
    const encodedString = encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (match, p1) =>
        String.fromCharCode(parseInt(p1, 16)),
    );

    return btoa(encodedString)
        .replace('+', '-')
        .replace('/', '_');
}

/**
 * Track when a batch that contains the timestamp param has been saved to IndexedDB
 * @param {Number} timestamp
 * @returns {Object} with the following properties:
 * @returns {Function} - checks if metadata contains the timestamp
 * @returns {Promise}
 */
export function getEventsFromChunkPayload (payload) {
    if (Array.isArray(payload)) {
        return payload;
    }

    if (payload && Array.isArray(payload.data)) {
        return payload.data;
    }

    return [];
}

export function chunkHasBootstrapEvents (events) {
    if (!events?.length) {
        return false;
    }

    let hasMeta = false;
    let hasFullSnapshot = false;

    for (const event of events) {
        if (event.type === RRWEB_META_EVENT_TYPE) {
            hasMeta = true;
        }
        if (event.type === RRWEB_FULL_SNAPSHOT_EVENT_TYPE) {
            hasFullSnapshot = true;
        }
        if (hasMeta && hasFullSnapshot) {
            return true;
        }
    }

    return false;
}

export function validateChunkPayload (payload, recordingId) {
    const events = WorkerUtils.getEventsFromChunkPayload(payload);
    if (!WorkerUtils.chunkHasBootstrapEvents(events)) {
        const suffix = recordingId ? ` (recordingId: ${recordingId})` : '';

        return {
            valid: false,
            error: `${WorkerUtils.UNRENDERABLE_CHUNK_ERROR}${suffix}`,
        };
    }

    return { valid: true };
}

export async function validateChunkBlob (blob, recordingId) {
    const json = JSON.parse(await blob.text());

    return WorkerUtils.validateChunkPayload(json, recordingId);
}

export function trackBatchSaveStatus (timestamp) {
    let resolve;
    let reject;
    const promise = new Promise((res, rej) => {
        resolve = res;
        reject = rej;
    });

    return {
        checkMetadata (metadata) {
            if (timestamp >= metadata.batchStartTime && timestamp <= metadata.batchEndTime) {
                resolve();
            }
        },
        rejectForMetadata (metadata, error) {
            if (timestamp >= metadata.batchStartTime && timestamp <= metadata.batchEndTime) {
                reject(error);
            }
        },
        onComplete: promise,
    };
}

/**
 * Checks if a given timestamp is "void" time aka a timestamp that doesn't belong to any chunk
 * @param {Number} timestamp
 * @param {import('./helpers/common.util').chunk[]} chunks
 * @returns {Boolean} true if the timestamp is "void" time, false otherwise
 */
export function isVoidTime ({ timestamp, chunks }) {
    return WorkerUtils.findChunkIndex({ timestamp, chunks }) === -1;
}
